<script setup lang="ts">
import { ref } from 'vue';
import { PrimaryButton } from '@hfc/ui-core';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { useSmallBusinessStore } from '@/application/smallBusiness/infrastructure/composition/useSmallBusinessStore';
import LetUsHelpSection from '@/application/shared/infrastructure/components/LetUsHelpSection.vue';
import NavigationModals from '@/application/smallBusiness/infrastructure/components/NavigationModals.vue';
import SectionLetsBegin from '@/application/smallBusiness/infrastructure/components/sections/SectionLetsBegin.vue';

interface EmitInterface {
  (event: 'nextSection'): void;
}

const emits = defineEmits<EmitInterface>();

defineOptions({ inheritAttrs: false });

const { smallBusinessStore, smallBusinessState } = useSmallBusinessStore();

const letsBeginRef = ref<InstanceType<typeof SectionLetsBegin> | null>(null);

const sectionSubmit = async (): Promise<boolean> => {
  return (await letsBeginRef.value?.sectionSubmit()) ?? false;
};

const checkSaveAndExitInfo = async (email: string): Promise<void> => {
  if (email !== smallBusinessState.value.application.company.primary_contact?.email) {
    await smallBusinessStore.updateCompany({ primary_contact: { ...smallBusinessState.value.application.company.primary_contact, email: email } });
  }

  if (smallBusinessState.value.application.applicants.length === 0 && smallBusinessState.value.companySize > 0) {
    await smallBusinessStore.addEmployee(smallBusinessState.value.companySize);
  }

  await smallBusinessStore.saveApplicationOnLocalStorage();
};

defineExpose({ sectionSubmit });
</script>
<template>
  <div class="relative w-full min-h-screen">
    <div class="relative z-10 w-full max-w-3xl mx-auto pt-36 pb-16 lg:pt-36 lg:pb-24">
      <div class="relative flex flex-col bg-white rounded-lg border border-slate-300 shadow-2xl p-4 sm:p-6">
        <div class="[&>div]:shadow-none [&>div]:p-0 [&_h3]:text-center">
          <SectionLetsBegin ref="letsBeginRef" v-bind="$attrs" />
        </div>
        <div class="flex justify-center mt-6">
          <PrimaryButton @click="emits('nextSection')" size="xl">Begin quote</PrimaryButton>
        </div>
        <div class="flex flex-col-reverse gap-4 mt-6 sm:flex-row sm:justify-between sm:items-center">
          <LetUsHelpSection />
          <NavigationModals class="items-center" :show-save-and-exit="true" :show-clear-and-restart="true" :application-type="APPLICATION_TYPES.SMALL_BUSINESS" :uuid="smallBusinessState.application.uuid" :email="smallBusinessState.application.company.primary_contact?.email" :application="smallBusinessState.application" :save-and-exit-callback="checkSaveAndExitInfo" />
        </div>
      </div>
    </div>
  </div>
</template>
